import { effectiveParentId, indexProjects } from '../model/project-tree';
import type { ProjectRecord } from '../model/types';

/** One top-level project of a rolled-up ranking (project hierarchy, data dictionary §3). */
export interface ProjectRollup {
  readonly projectId: string;
  /** Own minutes plus the minutes of all children. */
  readonly totalMin: number;
  /** Minutes recorded on the project itself, shown as "未细分" when it has children. */
  readonly ownMin: number;
  /** Children with minutes, largest first (ties by id); children without minutes are left out. */
  readonly children: readonly { readonly projectId: string; readonly minutes: number }[];
}

export interface RollupResult {
  /** Top-level projects with minutes, largest total first (ties by id). */
  readonly projects: readonly ProjectRollup[];
  /** Minutes of projects that are missing or deleted. */
  readonly unknownMin: number;
}

/**
 * Roll `byProject` (minutes per project id, e.g. `Summary.byProject`) up to top-level projects:
 * a child's minutes add to its effective parent, a project without parent stands for itself.
 * Computed on read, so moving a project under another one changes past statistics too.
 */
export function rollUpByParent(
  byProject: Readonly<Record<string, number>>,
  projects: readonly ProjectRecord[],
): RollupResult {
  const byId = indexProjects(projects);
  const groups = new Map<string, { own: number; children: Map<string, number> }>();
  let unknownMin = 0;
  for (const [projectId, minutes] of Object.entries(byProject)) {
    const project = byId.get(projectId);
    if (project === undefined) {
      unknownMin += minutes;
      continue;
    }
    const parentId = effectiveParentId(project, byId);
    const topId = parentId ?? projectId;
    const group = groups.get(topId) ?? { own: 0, children: new Map<string, number>() };
    if (parentId === null) group.own += minutes;
    else group.children.set(projectId, (group.children.get(projectId) ?? 0) + minutes);
    groups.set(topId, group);
  }
  const byMinutesThenId = (a: { id: string; min: number }, b: { id: string; min: number }): number =>
    b.min - a.min || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  const rolled = [...groups.entries()].map(([projectId, group]): ProjectRollup => {
    const children = [...group.children.entries()]
      .map(([id, min]) => ({ id, min }))
      .sort(byMinutesThenId)
      .map(({ id, min }) => ({ projectId: id, minutes: min }));
    const childMin = children.reduce((sum, child) => sum + child.minutes, 0);
    return { projectId, totalMin: group.own + childMin, ownMin: group.own, children };
  });
  const projectsRanked = rolled
    .map((entry) => ({ entry, id: entry.projectId, min: entry.totalMin }))
    .sort(byMinutesThenId)
    .map(({ entry }) => entry);
  return { projects: projectsRanked, unknownMin };
}
